import {memo, useMemo} from 'react'

import classNames from 'classnames/bind'

import SummaryText from '$components/insurance/my/home/diagnosis/SummaryText'
import {또래대비_보장조건, 사망입원후유장해_목록, 암뇌심_목록} from '$constants/insurance/diagnosis'
import {useUserInfoContext} from '$contexts/member/userInfoStatus'
import {useDiagnosisCoverage} from '$hooks/insurance/my/home/diagnosis/useDiagnosisCoverage'
import {GRAPH_LABEL} from '$share/constants/insurance'

import styles from './InsuranceEstimate.module.scss'

const cx = classNames.bind(styles)

export const InsuranceCoverageCheckSummaryText = memo(function InsuranceCoverageCheckSummaryText() {
    const {coverages} = useDiagnosisCoverage()
    const {userInfo} = useUserInfoContext()

    const 나이대 = useMemo(() => Math.floor(userInfo.age / 10) * 10, [userInfo.age])

    // 실손: 부족/매우부족 & 암뇌심: 부족/매우부족 , 사망입원후유장해는 고려하지않음
    const 실손_암뇌심_부족 = useMemo(() => {
        return (
            <>
                꼭 가입해야 하는 <b>실손</b> 보장,
                <br />
                주요 3대 질환, <b>암 · 뇌 · 심장</b> 보장이 부족해요.
            </>
        )
    }, [])

    // 실손: 충분 & 암뇌심: 부족/매우부족 , 사망입원후유장해에 따라 문구 노출
    const 암뇌심_부족 = useMemo(() => {
        const 암뇌심_부족_보장목록 = coverages
            .filter((elem) => 암뇌심_목록.includes(elem.labelName) && elem.condition !== 또래대비_보장조건.충분)
            .map((elem) => elem.labelName)

        const 모두부족 = 암뇌심_부족_보장목록.length === 암뇌심_목록.length
        const 조건문 = 모두부족 ? ',' : ' 중'

        const 사망입원후유장해_보장목록 = coverages.filter((elem) => 사망입원후유장해_목록.includes(elem.labelName))

        const 모두_미가입 =
            사망입원후유장해_보장목록.filter((elem) => elem.myAmount === 0).length === 사망입원후유장해_보장목록.length
        if (모두_미가입) {
            return (
                <>
                    주요 3대 질환{조건문} <b>{암뇌심_부족_보장목록.join(' · ')}</b> 보장이 부족해요.
                    <br />
                    {나이대}대에 맞는 다른 보장도 있으면 좋아요.
                </>
            )
        }

        const 사망입원후유장해_부족_보장목록 = 사망입원후유장해_보장목록
            .filter((elem) => elem.condition !== 또래대비_보장조건.충분)
            .map((elem) => elem.labelName)
        if (사망입원후유장해_부족_보장목록.length > 0) {
            return (
                <>
                    주요 3대 질환{조건문} <b>{암뇌심_부족_보장목록.join(' · ')}</b> 보장이 부족해요.
                    <br />
                    <b>{사망입원후유장해_부족_보장목록.join(' · ')}</b> 보장도 또래보다 적어요.
                </>
            )
        }

        return (
            <>
                주요 3대 질환{조건문} <b>{암뇌심_부족_보장목록.join(' · ')}</b> 보장이 부족해요.
            </>
        )
    }, [coverages, 나이대])

    // 실손: 충분, 암뇌심: 충분, 사망입원후유장해만 가지고 문구 노출
    const 실손_암뇌심_모두충분 = useMemo(() => {
        const 사망입원후유장해_보장목록 = coverages.filter((elem) => 사망입원후유장해_목록.includes(elem.labelName))

        const 모두_미가입 =
            사망입원후유장해_보장목록.filter((elem) => elem.myAmount === 0).length === 사망입원후유장해_보장목록.length
        if (모두_미가입) {
            return (
                <>
                    필수적인 실손, 3대 질환 보장에 충분히 대비했어요.
                    <br />
                    {나이대}대에 맞는 다른 보장도 있으면 좋아요.
                </>
            )
        }

        const 사망입원후유장해_부족_보장목록 = 사망입원후유장해_보장목록
            .filter((elem) => elem.condition !== 또래대비_보장조건.충분)
            .map((elem) => elem.labelName)
        if (사망입원후유장해_부족_보장목록.length > 0) {
            return (
                <>
                    필수적인 실손, 3대 질환 보장에 충분히 대비했어요. <br />
                    <b>{사망입원후유장해_부족_보장목록.join(' · ')}</b> 보장은 또래보다 부족해요.
                </>
            )
        }

        return <>{나이대}대에 준비해야 할 모든 상황에 잘 대비했어요. 보험료가 적절하다면 걱정 없어요.</>
    }, [coverages, 나이대])

    const text = useMemo(() => {
        const 실손_부족여부 =
            coverages.filter((elem) => elem.labelName === GRAPH_LABEL.실손 && elem.condition !== 또래대비_보장조건.충분)
                .length > 0

        const 암뇌심_부족_보장목록 =
            coverages.filter(
                (elem) => 암뇌심_목록.includes(elem.labelName) && elem.condition !== 또래대비_보장조건.충분,
            ).length > 0

        if (실손_부족여부 && 암뇌심_부족_보장목록) {
            return 실손_암뇌심_부족
        }

        if (암뇌심_부족_보장목록) {
            return 암뇌심_부족
        }

        return 실손_암뇌심_모두충분
    }, [coverages, 실손_암뇌심_모두충분, 실손_암뇌심_부족, 암뇌심_부족])

    return <SummaryText articleClassName={cx('summary', 'type-connect')} text={text} />
})
